import type { Cpc25AttentionCase, Cpc25ReportRow } from '../../../../types/LegalCase';

// Motivos da lista "Casos que merecem atenção" do relatório CPC 25. Puro e testável; o ícone e a
// cor ficam no componente. A ordem é a mesma do backend (`ATTENTION_PRIORITY` no
// cpc25_report_query.py): primeiro o que infla a provisão hoje, depois o que põe a base em dúvida,
// por fim o que é só desvio do padrão. Explicação para o usuário no tópico de ajuda
// `report.attention`.

export type AttentionSignal =
    | 'closedStillProvisioning'
    | 'allTitlesSettled'
    | 'paidStillProvisioning'
    | 'overrideAboveCalculated'
    | 'claimValueDiverges'
    | 'debtorHasMultipleOpenCollectionCases'
    | 'creditorEntityUndefined'
    | 'overridden'
    | 'offSuggestion';

export const ATTENTION_SIGNALS: AttentionSignal[] = [
    'closedStillProvisioning',
    'allTitlesSettled',
    'paidStillProvisioning',
    'overrideAboveCalculated',
    'claimValueDiverges',
    'debtorHasMultipleOpenCollectionCases',
    'creditorEntityUndefined',
    'overridden',
    'offSuggestion',
];

// Sinais que mexem com a provisão de hoje: o componente pinta de vermelho.
export const CRITICAL_SIGNALS: ReadonlySet<AttentionSignal> = new Set<AttentionSignal>([
    'closedStillProvisioning',
    'allTitlesSettled',
    'paidStillProvisioning',
    'overrideAboveCalculated',
]);

const REASON_TEXT: Record<AttentionSignal, (c: Cpc25AttentionCase) => string> = {
    closedStillProvisioning: () => 'Encerrado, mas ainda provisiona — reclassificar',
    allTitlesSettled: () => 'Títulos todos quitados na cobrança — reclassificar ou encerrar',
    paidStillProvisioning: (c) =>
        `Recebeu ${c.paidTotalFormatted || 'pagamento'} e segue provisionando o valor cheio — reclassificar ou ajustar`,
    overrideAboveCalculated: () => 'Provisão ajustada acima da calculada',
    claimValueDiverges: (c) =>
        `Valor da causa (${c.claimValueFormatted || '—'}) não confere com os títulos (${c.titlesTotalFormatted || '—'})`,
    debtorHasMultipleOpenCollectionCases: () =>
        'Devedor com mais de um caso de cobrança aberto — podem ser o mesmo caso duplicado',
    creditorEntityUndefined: () => 'Veio da cobrança com a entidade do grupo a definir — escolher a entidade',
    overridden: () => 'Provisão ajustada manualmente',
    offSuggestion: () => 'Percentual fora do padrão sugerido',
};

export const activeSignals = (c: Cpc25AttentionCase): AttentionSignal[] =>
    ATTENTION_SIGNALS.filter((signal) => Boolean(c[signal]));

// O motivo principal é o de maior prioridade. "Provisão ajustada manualmente" some quando o
// ajuste já aparece como "acima da calculada" — é o mesmo fato dito duas vezes.
export const attentionReasons = (c: Cpc25AttentionCase): string[] => {
    const signals = activeSignals(c).filter(
        (signal) => !(signal === 'overridden' && c.overrideAboveCalculated),
    );
    return signals.map((signal) => REASON_TEXT[signal](c));
};

export const primarySignal = (c: Cpc25AttentionCase): AttentionSignal | null =>
    activeSignals(c)[0] ?? null;

// Títulos dos ícones por linha da tabela de polo (contadores do bucket).
const ROW_SIGNAL_TITLE: Record<AttentionSignal, (n: number) => string> = {
    closedStillProvisioning: (n) => `${n} caso(s) encerrado(s) que ainda provisionam — reclassificar`,
    allTitlesSettled: (n) => `${n} caso(s) com todos os títulos quitados na cobrança`,
    paidStillProvisioning: (n) => `${n} caso(s) que já receberam pagamento e seguem provisionando`,
    overrideAboveCalculated: (n) => `${n} caso(s) com provisão ajustada acima da calculada`,
    claimValueDiverges: (n) => `${n} caso(s) com valor da causa que não confere com os títulos`,
    debtorHasMultipleOpenCollectionCases: (n) =>
        `${n} caso(s) de devedor com mais de um caso de cobrança aberto`,
    creditorEntityUndefined: (n) => `${n} caso(s) vindos da cobrança com a entidade do grupo a definir`,
    overridden: (n) => `${n} caso(s) com provisão ajustada manualmente`,
    offSuggestion: (n) => `${n} caso(s) com percentual fora do padrão`,
};

// Contador opcional (sinal novo ausente numa resposta anterior) conta como zero.
const signalCount = (row: Cpc25ReportRow, signal: AttentionSignal): number => row[signal] ?? 0;

export const rowSignals = (row: Cpc25ReportRow): { signal: AttentionSignal; title: string }[] =>
    ATTENTION_SIGNALS
        .filter((signal) => signalCount(row, signal) > 0)
        .map((signal) => ({ signal, title: ROW_SIGNAL_TITLE[signal](signalCount(row, signal)) }));
